import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { Download, FileText, FileSpreadsheet, Database, Loader2 } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ExportButton = ({ type = 'table', tableName = '', className = '' }) => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);

  const handleExport = async (format, period = null) => {
    setLoading(true);
    try {
      const headers = getAuthHeaders();
      let url = '';
      let filename = '';

      if (type === 'pdf') {
        url = `${API_URL}/export/pdf/resumen?period=${period}`;
        filename = `aircom_resumen_${period}.pdf`;
      } else if (format === 'csv') {
        url = `${API_URL}/export/csv/${tableName}`;
        filename = `aircom_${tableName}.csv`;
      } else if (format === 'sql') {
        url = `${API_URL}/export/sql/${tableName}`;
        filename = `aircom_${tableName}.sql`;
      }

      const response = await fetch(url, { headers });
      
      if (!response.ok) {
        throw new Error('Error al exportar');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      toast.success(`Exportado exitosamente: ${filename}`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Error al exportar los datos');
    } finally {
      setLoading(false);
    }
  };

  if (type === 'pdf') {
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" className={className} disabled={loading}>
            {loading ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <FileText className="w-4 h-4 mr-2 text-red-500" />
            )}
            Exportar PDF
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent>
          <DropdownMenuLabel>Resumen en PDF</DropdownMenuLabel>
          <DropdownMenuSeparator />
          <DropdownMenuItem onClick={() => handleExport('pdf', 'monthly')}>
            <FileText className="w-4 h-4 mr-2 text-red-500" />
            Resumen Mensual
          </DropdownMenuItem>
          <DropdownMenuItem onClick={() => handleExport('pdf', 'annual')}>
            <FileText className="w-4 h-4 mr-2 text-red-500" />
            Resumen Anual
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" className={className} disabled={loading}>
          {loading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          Exportar
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent>
        <DropdownMenuLabel>Formato de exportación</DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={() => handleExport('csv')}>
          <FileSpreadsheet className="w-4 h-4 mr-2 text-green-600" />
          Exportar CSV
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleExport('sql')}>
          <Database className="w-4 h-4 mr-2 text-blue-600" />
          Exportar SQL
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ExportButton;
